import React, { FC, useEffect } from 'react';
import moment from 'moment/moment';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, UseFormOptions } from 'react-hook-form';

import { fc } from 'i18n';

import { close as closeSign } from 'assets/icons';
import Button from 'core/react/general/form/Button';
import { Preloader } from 'components/atoms/preloader';
import { SvgWrapper } from 'components/atoms/svg-wrapper';
import { IIssuePayload, IMultipleEditForm } from 'types/tep';
import { editModalSelector } from 'core/redux/selectors/tep';
import { useEditColumns } from 'hooks/tep/leases/useEditColumns';
import { ModalWrapper } from 'components/molecules/modal-wrapper';
import { InfoTable } from 'components/organisms/tables/info-table';
import { actions as leasesActions } from 'core/redux/api/veta/tep/leases';

import styles from './styles.module.css';

const FORM_OPTIONS: UseFormOptions<IMultipleEditForm> = {
  mode: 'onChange',
};

export const EditModal: FC = () => {
  const dispatch = useDispatch();

  const { isOpen, isLoading, error, selected } = useSelector(editModalSelector);

  const formMethods = useForm<IMultipleEditForm>(FORM_OPTIONS);

  const { control, reset, handleSubmit } = formMethods;

  const { fields, remove } = useFieldArray({ control, name: 'leases', keyName: 'fieldId' });

  const removeLease = (fieldIndex: number) => remove(fieldIndex);

  const columns = useEditColumns(removeLease);

  const onSubmit = handleSubmit(({ leases }: IMultipleEditForm) => {
    const issued: Record<string, IIssuePayload> = {};

    leases.forEach((lease, index) => {
      const { email, plateNumber, startOn, endOn } = lease;
      const start = moment(startOn).format('YYYY-MM-DD');
      const end = moment(endOn).format('YYYY-MM-DD');

      issued[fields[index].id] = { company: fields[index].company, email, plateNumber, startOn: start, endOn: end };
    });

    dispatch(leasesActions.updateMultiple.request({ data: { leases: issued } }));
  });

  const setLeases = () => {
    const formLeases = selected.map(({ id, device, lease }) => {
      const { name, uri } = device;
      const { company, email, plateNumber, startOn, endOn } = lease;

      return { id, name, uri, company, email, plateNumber, startOn, endOn };
    });

    reset({ leases: formLeases });
  };

  const close = () => dispatch(leasesActions.close());

  useEffect(() => {
    if (isOpen) {
      setLeases();
    }
  }, [isOpen]);

  return (
    <ModalWrapper>
      <FormProvider {...formMethods}>
        <form className={styles.content} onSubmit={onSubmit}>
          <span className={styles.close} onClick={close}>
            <SvgWrapper width={26} height={26} details={closeSign} />
          </span>
          <div className={styles.title}>{fc('edit')}</div>
          {/*@ts-ignore*/}
          <InfoTable isLoading={false} height={240} columns={columns} data={fields} />
          <div className={styles.totalCount}>{`Total No. Of Entries : ${fields.length}`}</div>
          <div className={styles.footer}>
            <div className={styles.error}>{error || ''}</div>
            <Button className={`${styles.submit} active`} type="submit">
              <span>{fc('save')}</span>
            </Button>
          </div>
        </form>
        {isLoading && <Preloader />}
      </FormProvider>
    </ModalWrapper>
  );
};
